import { useState } from 'react';
import { Button } from '../components/atoms/Button/Button';
import './AquariumDetailPage.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useReturnFocus } from '../hooks/useReturnFocus';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { useTranslation } from '../i18n/useTranslation';
import { TaskList } from '../components/tasks/TaskList';
import { TaskCalendar } from '../components/tasks/TaskCalendar';
import { TaskForm } from '../components/tasks/TaskForm';
import type { TaskFormSubmitValues } from '../components/tasks/TaskForm';
import { TaskDetail } from '../components/tasks/TaskDetail';
import { useTasks, useCompleteTask, useSkipTask, useCreateTask, useUpdateTask } from '../hooks/useTasks';
import { useAiSuggestions, useAcceptSuggestion, useDismissSuggestion } from '../hooks/useAiSuggestions';
import { useToast } from '../stores/toastStore';
import type { AiSuggestion } from '../types/aiSuggestion';
import { isDosingPlanTask, type Task, type CreateTaskInput, type RecurrenceScope, type UpdateTaskInput } from '../types/task';
import { ZeovitRecommendationCardSection } from '../components/reef-intelligence/ZeovitRecommendationCardSection';
import { useZeovitAvailability } from '../hooks/useZeovitAvailability';

function buildCreateTaskInput(values: TaskFormSubmitValues): CreateTaskInput {
  return {
    title: values.title,
    description: values.description || null,
    priority: values.priority,
    category: values.category,
    dueDate: values.dueDate || null,
    dueTime: values.dueTime || null,
    recurrenceRule: values.recurrenceRule ?? null,
    emailReminder: values.emailReminder ?? false,
  };
}

function buildUpdateTaskInput(values: TaskFormSubmitValues, includeStatus: boolean): UpdateTaskInput {
  return {
    title: values.title,
    description: values.description || null,
    priority: values.priority,
    category: values.category,
    dueDate: values.dueDate || null,
    dueTime: values.dueTime || null,
    emailReminder: values.emailReminder ?? false,
    ...(includeStatus ? { status: values.status } : {}),
    // Undefined when only one occurrence is edited: the series rule stays as it is.
    ...(values.recurrenceRule !== undefined ? { recurrenceRule: values.recurrenceRule } : {}),
  };
}

interface EditTarget {
  task: Task;
  scope: RecurrenceScope | undefined;
}

/** Focus returns here after the test session (TestSessionPage passes focusElementId back). */
const LOG_TESTS_BUTTON_ID = 'aquarium-log-tests';

export function AquariumDetailPage() {
  const { aquariumId } = useParams<{ aquariumId: string }>();
  const id = aquariumId ?? '';
  const { showToast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  useReturnFocus();

  const [showCreateForm, setShowCreateForm] = useState(false);
  /** Due date prefilled in the create form (a day tapped in the calendar). */
  const [createDueDate, setCreateDueDate] = useState<string | undefined>(undefined);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskView, setTaskView] = useState<'list' | 'calendar'>('calendar');

  const { grouped, isLoading, isError, refetch } = useTasks(id);
  const { suggestions } = useAiSuggestions({ aquariumId: id });
  const createMutation = useCreateTask(id);
  const updateMutation = useUpdateTask(id);
  const completeMutation = useCompleteTask(id);
  const skipMutation = useSkipTask(id);
  const { acceptSuggestion } = useAcceptSuggestion();
  // From the shared aquarium list query (already loaded by the app root) — no extra fetch.
  const zeovitAvailability = useZeovitAvailability(id);
  const { dismissSuggestion } = useDismissSuggestion();

  function openCreateForm(dueDate?: string) {
    setCreateDueDate(dueDate);
    setShowCreateForm(true);
  }

  function handleLogTests() {
    if (!aquariumId) {
      return;
    }
    navigate(`/aquariums/${aquariumId}/measurements/session`, {
      state: { returnTo: location.pathname, invokerElementId: LOG_TESTS_BUTTON_ID },
    });
  }

  function handleCompleteTask(taskId: string, notes?: string) {
    const task = [...grouped.overdue, ...grouped.today, ...grouped.upcoming].find((item) => item.id === taskId);
    // A dose of a manual dosing plan is logged when it is ticked off.
    const doneMessage = task && isDosingPlanTask(task) ? t('tasks.toast.dosingLogged') : t('tasks.toast.completed');
    completeMutation.mutateAsync({ taskId, input: notes ? { completionNotes: notes } : undefined })
      .then(() => showToast(doneMessage, 'success'))
      .catch(() => showToast(t('tasks.toast.actionFailed'), 'error'));
  }

  function handleSkipTask(taskId: string) {
    skipMutation.mutateAsync({ taskId })
      .then(() => showToast(t('tasks.toast.skipped'), 'success'))
      .catch(() => showToast(t('tasks.toast.actionFailed'), 'error'));
  }

  function handleAcceptSuggestion(suggestionId: string) {
    void acceptSuggestion({ suggestionId, aquariumId: id });
  }

  function handleDismissSuggestion(suggestionId: string) {
    void dismissSuggestion({ suggestionId, aquariumId: id });
  }

  async function handleCreate(values: TaskFormSubmitValues) {
    await createMutation.mutateAsync(buildCreateTaskInput(values));
    showToast(t('tasks.toast.created'), 'success');
  }

  async function handleUpdate(target: EditTarget, values: TaskFormSubmitValues) {
    const recurring = target.task.isRecurring ?? Boolean(target.task.recurrenceRule);
    const includeStatus = !recurring || target.scope === 'occurrence';
    await updateMutation.mutateAsync({
      taskId: target.task.id,
      input: buildUpdateTaskInput(values, includeStatus),
      scope: recurring ? target.scope : undefined,
    });
    showToast(t('tasks.toast.updated'), 'success');
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="wide">
          <PageHeader
            title={t('tasks.title')}
            description={t('tasks.emptyDescription')}
            actions={
              <>
                <div className="aquarium-detail-page__view-bar" role="group" aria-label={t('tasks.view.label')}>
                  <Button
                    type="button"
                    variant="ghost"
                    className={`aquarium-detail-page__view-btn ${taskView === 'calendar' ? 'aquarium-detail-page__view-btn--active' : ''}`}
                    onClick={() => setTaskView('calendar')}
                    aria-pressed={taskView === 'calendar'}
                  >
                    {t('tasks.view.calendar')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className={`aquarium-detail-page__view-btn ${taskView === 'list' ? 'aquarium-detail-page__view-btn--active' : ''}`}
                    onClick={() => setTaskView('list')}
                    aria-pressed={taskView === 'list'}
                  >
                    {t('tasks.view.list')}
                  </Button>
                </div>
                <Button id={LOG_TESTS_BUTTON_ID} type="button" variant="secondary" onClick={handleLogTests} disabled={!aquariumId}>
                  {t('aquariumDetail.logTests.cta')}
                </Button>
                <Button type="button" variant="primary" onClick={() => openCreateForm()}>
                  {t('tasks.new')}
                </Button>
              </>
            }
          />

          {taskView === 'list' ? (
            <TaskList
              aquariumId={id}
              overdueTasks={grouped.overdue}
              todayTasks={grouped.today}
              upcomingTasks={grouped.upcoming}
              completedTasks={grouped.completed}
              aiSuggestions={suggestions}
              isLoading={isLoading}
              isError={isError}
              onRetry={() => void refetch()}
              onCompleteTask={handleCompleteTask}
              onSkipTask={handleSkipTask}
              onOpenTask={(task) => setSelectedTask(task)}
              onCreateTask={() => openCreateForm()}
              onAcceptSuggestion={handleAcceptSuggestion}
              onDismissSuggestion={handleDismissSuggestion}
              onOpenSuggestion={(_s: AiSuggestion) => {}}
            />
          ) : (
            <TaskCalendar
              aquariumId={id}
              onOpenTask={(task) => setSelectedTask(task)}
              onCreateTask={(date) => openCreateForm(date)}
            />
          )}

          {showCreateForm && (
            <TaskForm
              aquariumId={id}
              initialDueDate={createDueDate}
              onSubmit={handleCreate}
              isSubmitting={createMutation.isPending}
              onClose={() => setShowCreateForm(false)}
            />
          )}

          {editTarget && (
            <TaskForm
              aquariumId={id}
              task={editTarget.task}
              scope={editTarget.scope}
              onSubmit={(values) => handleUpdate(editTarget, values)}
              isSubmitting={updateMutation.isPending}
              onClose={() => setEditTarget(null)}
            />
          )}

          {selectedTask && (
            <TaskDetail
              taskId={selectedTask.id}
              onClose={() => setSelectedTask(null)}
              onEdit={(task, scope) => {
                setSelectedTask(null);
                setEditTarget({ task, scope });
              }}
              onDeleted={() => showToast(t('tasks.toast.deleted'), 'success')}
            />
          )}

          {/* ZeoVit card only for aquariums whose supply system is ZeoVit (#1784). */}
          {zeovitAvailability === 'available' ? <ZeovitRecommendationCardSection aquariumId={id} /> : null}

        </PageContent>
      }
    />
  );
}

export default AquariumDetailPage;
